import { ChangeDetectorRef, Component, OnDestroy, OnInit } from "@angular/core";
import { FormBuilder, FormGroup, Validators } from "@angular/forms";
import { NgbModal, NgbModalRef } from "@ng-bootstrap/ng-bootstrap";
import Swal from "sweetalert2";
import { VerificacionRutValidator } from "src/app/directives/verifica-rut.directive";
import { Emisor } from "src/app/models/Emisor/Emisor";
import { PlanUsuario } from "src/app/models/PlanesUsuario/PlanUsuario";
import { Sucursal } from "src/app/models/Sucursal";
import { Usuario } from "src/app/models/Usuario/Usuario";
import { AuthService } from "src/app/modules/auth";
import { LocalStorageService } from "src/app/services/local-storage.service";
import { PermissionService } from "src/app/services/permission.service";
import { TokenStorageService } from "src/app/services/token-storage.service";
import { UsuarioService } from "src/app/services/usuario.service";
import { FormrulesConfigService } from "src/app/shared/configuration/formrules-config.service";
import { MiCuentaAvatar, MiCuentaResumen, MiCuentaService, SucursalStats } from "src/app/services/mi-cuenta.service";
import { SaldoService } from "src/app/services/saldo.service";
import { SocialOAuthProvider } from "src/app/modules/auth/_models/social-oauth-provider.enum";
import { environment } from "src/environments/environment";
import { ActivatedRoute } from "@angular/router";

type MiCuentaTab = "resumen" | "datos" | "seguridad" | "metodos" | "empresas" | "cambio-usuario-base" | "billetera";

export function esUsuarioBaseDePlanes(usuarioId: string, planes: PlanUsuario[]): boolean {
   const idNormalizado = String(usuarioId || "").trim().toLowerCase();
   return Boolean(idNormalizado) && (planes || []).some(
      (plan) => String(plan?.usuarioId || "").trim().toLowerCase() === idNormalizado
   );
}

export function puedeMostrarBilletera(esUsuarioBase: boolean, tieneCuentaSaldo: boolean): boolean {
   return esUsuarioBase && tieneCuentaSaldo;
}

interface AccessMethod {
   id: string;
   name: string;
   description: string;
   iconClass: string;
   iconColor: string;
   linked: boolean;
   principal?: boolean;
   accountLabel?: string;
   identityId?: string;
}

interface EmpresaPermiso {
   emisor: Emisor;
   sucursales: Sucursal[];
}

@Component({
   selector: "app-mi-cuenta",
   templateUrl: "./mi-cuenta.component.html",
   styleUrls: ["./mi-cuenta.component.scss"],
})
export class MiCuentaComponent implements OnInit, OnDestroy {
   public loading = false;
   public activeTab: MiCuentaTab = "resumen";
   private readonly oauthLinkStatusKey = "oauth_link_status";
   private readonly oauthLinkMessageKey = "oauth_link_message";
   private readonly oauthLinkResultQueryParam = "oauth_result";
   private readonly oauthLinkResultQueryValue = "link";

   public currentUser: Usuario = new Usuario();
   public currentUserId: string;
   public emisorId: string;
   public esUsuarioBase = false;
   public tieneCuentaSaldo = false;

   get puedeVerBilletera(): boolean {
      return puedeMostrarBilletera(this.esUsuarioBase, this.tieneCuentaSaldo);
   }

   public initials = "";
   public emisores: Emisor[] = [];
   public sucursales: Sucursal[] = [];
   public empresasPermisos: EmpresaPermiso[] = [];

   public datosEditMode = false;
   public empresasMaxResumen = 3;
   public empresaSeleccionadaId: string;
   /** Mapa sucursalId -> stats (usuarios y DTEs del mes actual) */
   public sucursalStatsMap: { [sucursalId: string]: SucursalStats } = {};

   public usuarioForm: FormGroup;
   public passForm: FormGroup;
   private passModalRef: NgbModalRef;
   public cuentaResumen: MiCuentaResumen;
   public emailVerificado = false;
   public verificationCode = "";
   public verificationStep: "idle" | "awaiting-code" = "idle";
   public verificationDigits: string[] = ["", "", "", "", "", ""];
   public readonly verificationDurationMinutes = 1;
   public verificationExpiresAt: Date | null = null;
   public verificationRemainingSeconds = 0;
   private verificationTimer: any = null;

   get verificationCodeValue(): string {
      return this.verificationDigits.join("");
   }

   get formattedVerificationRemaining(): string {
      const seconds = Math.max(0, this.verificationRemainingSeconds);
      const m = Math.floor(seconds / 60);
      const s = seconds % 60;
      return `${m.toString().padStart(2, "0")}:${s.toString().padStart(2, "0")}`;
   }

   get verificationExpired(): boolean {
      return this.verificationStep === "awaiting-code" && this.verificationExpiresAt !== null && this.verificationRemainingSeconds <= 0;
   }

   get verificationRemainingIsLow(): boolean {
      return this.verificationStep === "awaiting-code" && this.verificationRemainingSeconds > 0 && this.verificationRemainingSeconds <= 60;
   }

   get tienePasswordConfigurada(): boolean {
      return this.cuentaResumen?.tienePasswordConfigurada === true;
   }

   get passwordActionLabel(): string {
      return this.tienePasswordConfigurada ? "Cambiar contraseña" : "Configurar contraseña";
   }

   get passwordStatusTitle(): string {
      return this.tienePasswordConfigurada ? "Contraseña configurada" : "Contraseña no configurada";
   }

   get passwordStatusDetail(): string {
      return this.tienePasswordConfigurada
         ? "Método de acceso: correo y contraseña"
         : "Configura una contraseña para poder ingresar también con tu correo.";
   }

   public accessMethods: AccessMethod[] = [
      {
         id: "email",
         name: "Correo y contraseña",
         description: "Configura una contraseña para ingresar con tu correo",
         iconClass: "flaticon-email",
         iconColor: "#4782e1",
         linked: false,
         principal: false,
         accountLabel: "",
      },
      {
         id: "google",
         name: "Google",
         description: "Inicia sesión con tu cuenta de Google",
         iconClass: "fab fa-google",
         iconColor: "#ea4335",
         linked: false,
      },
      {
         id: "linkedin",
         name: "LinkedIn",
         description: "Inicia sesión con tu cuenta de LinkedIn",
         iconClass: "fab fa-linkedin",
         iconColor: "#0a66c2",
         linked: false,
      },
      {
         id: "github",
         name: "GitHub",
         description: "Inicia sesión con tu cuenta de GitHub",
         iconClass: "fab fa-github",
         iconColor: "#24292e",
         linked: false,
      },
      {
         id: "microsoft",
         name: "Microsoft",
         description: "Inicia sesión con tu cuenta de Microsoft",
         iconClass: "fab fa-microsoft",
         iconColor: "#0078d4",
         linked: false,
      },
   ];

   constructor(
      private formBuilder: FormBuilder,
      private tokenService: TokenStorageService,
      private userService: UsuarioService,
      private modalService: NgbModal,
      public permissions: PermissionService,
      public authService: AuthService,
      private miCuentaService: MiCuentaService,
      private formConfig: FormrulesConfigService,
      private localStorage: LocalStorageService,
      private ref: ChangeDetectorRef,
      private saldoService: SaldoService,
      private route: ActivatedRoute
   ) {
      const sessionUser = this.tokenService.getUser();
      this.currentUserId = sessionUser.id;
      this.emisorId = this.tokenService.getCompany();
      this.esUsuarioBase = esUsuarioBaseDePlanes(this.currentUserId, sessionUser.planes);
   }

   ngOnInit(): void {
      this.setInitialTabFromQuery();
      this.createForm();
      this.createPassForm();
      this.loadCurrentUser();
      this.loadMiCuenta();
      this.loadEstadoBilletera();
      this.loadEmpresasYPermisos();
      this.loadSucursalesStats();
      this.empresaSeleccionadaId = this.emisorId;
      this.showOAuthLinkResultIfAny();
   }

   loadSucursalesStats(): void {
      this.miCuentaService.getSucursalesStats().subscribe(
         (response) => {
            const map: { [sucursalId: string]: SucursalStats } = {};
            (response?.data || []).forEach((stat) => {
               map[stat.sucursalId] = stat;
            });
            this.sucursalStatsMap = map;
            this.ref.detectChanges();
         },
         () => {
            // Mantenemos el mapa vacío; la UI mostrará 0 / "-" como fallback.
         }
      );
   }

   getStatsForSucursal(sucursalId: string): SucursalStats | null {
      return this.sucursalStatsMap[sucursalId] || null;
   }

   ngOnDestroy(): void {
      this.stopVerificationTimer();
   }

   setTab(tab: MiCuentaTab): void {
      if (tab === "billetera" && !this.puedeVerBilletera) {
         this.activeTab = "resumen";
         return;
      }
      if (tab === "cambio-usuario-base" && !this.esUsuarioBase) {
         this.activeTab = "resumen";
         return;
      }

      this.activeTab = tab;
      if (tab !== "datos" && this.datosEditMode) {
         this.cancelEditDatos();
      }
   }

   private setInitialTabFromQuery(): void {
      const tab = this.parseTab(this.route.snapshot.queryParamMap.get("tab"));
      if (tab) {
         this.activeTab = tab;
      }
   }

   private parseTab(value: string | null): MiCuentaTab | null {
      const normalized = (value || "").toLowerCase();
      switch (normalized) {
         case "resumen":
         case "datos":
         case "seguridad":
         case "metodos":
         case "empresas":
            return normalized as MiCuentaTab;
         case "cambio-usuario-base":
            return this.esUsuarioBase ? normalized as MiCuentaTab : null;
         case "billetera":
            return this.puedeVerBilletera ? normalized as MiCuentaTab : null;
         default:
            return null;
      }
   }

   private loadEstadoBilletera(): void {
      if (!this.esUsuarioBase) {
         return;
      }

      this.saldoService.obtenerResumen().subscribe({
         next: (resumen) => {
            this.tieneCuentaSaldo = resumen.tieneCuenta;
            const requestedTab = (this.route.snapshot.queryParamMap.get("tab") || "").toLowerCase();
            if (requestedTab === "billetera" && this.puedeVerBilletera) {
               this.activeTab = "billetera";
            } else if (!this.puedeVerBilletera && this.activeTab === "billetera") {
               this.activeTab = "resumen";
            }
            this.ref.detectChanges();
         },
         error: () => {
            this.tieneCuentaSaldo = false;
            if (this.activeTab === "billetera") {
               this.activeTab = "resumen";
            }
            this.ref.detectChanges();
         },
      });
   }

   private showOAuthLinkResultIfAny(): void {
      const isOAuthLinkReturn = this.route.snapshot.queryParamMap.get(this.oauthLinkResultQueryParam) === this.oauthLinkResultQueryValue;
      const status = sessionStorage.getItem(this.oauthLinkStatusKey);
      const message = sessionStorage.getItem(this.oauthLinkMessageKey);
      sessionStorage.removeItem(this.oauthLinkStatusKey);
      sessionStorage.removeItem(this.oauthLinkMessageKey);

      if (!isOAuthLinkReturn || !status) {
         return;
      }

      Swal.fire({
         icon: status === "success" ? "success" : "error",
         title: status === "success" ? "Metodo vinculado" : "No fue posible vincular",
         text: message || (status === "success" ? "El metodo de acceso fue vinculado correctamente." : "Intente nuevamente."),
      });
   }

   get empresasResumen(): EmpresaPermiso[] {
      return this.empresasPermisos.slice(0, this.empresasMaxResumen);
   }

   get empresasResumenRestantes(): number {
      return Math.max(0, this.empresasPermisos.length - this.empresasMaxResumen);
   }

   get empresaSeleccionada(): EmpresaPermiso | undefined {
      return this.empresasPermisos.find((e) => e.emisor.emisorId === this.empresaSeleccionadaId);
   }

   onCambiarEmpresaSeleccionada(emisorId: string): void {
      this.empresaSeleccionadaId = emisorId;
   }

   loadCurrentUser(): void {
      this.loading = true;
      this.userService.getUser(this.currentUserId, this.emisorId).subscribe(
         (response) => {
            this.currentUser = response.data;
            this.computeInitials();
            this.setFormData(this.currentUser);
            this.accessMethods[0].accountLabel = this.currentUser.email;
            this.loading = false;
            this.ref.detectChanges();
         },
         () => {
            this.loading = false;
            this.ref.detectChanges();
            Swal.fire({
               title: "Error en datos",
               text: "No fue posible obtener los datos del usuario, favor intente nuevamente",
               icon: "error",
            });
         }
      );
   }

   loadMiCuenta(): void {
      this.miCuentaService.getResumen().subscribe(
         (response) => {
            this.cuentaResumen = response.data;
            this.emailVerificado = !!this.cuentaResumen && this.cuentaResumen.emailVerificado === true;
            this.failedAvatarUrls.clear();
            this.syncAccessMethods();
            this.syncPasswordFormValidators();
            this.ref.detectChanges();
         },
         () => {
            this.emailVerificado = false;
            this.ref.detectChanges();
         }
      );
   }

   syncAccessMethods(): void {
      const linkedProviders = new Map(
         (this.cuentaResumen?.metodosAcceso || []).map((method) => [method.proveedorCodigo, method])
      );
      this.accessMethods = this.accessMethods.map((method) => {
         if (method.id === "email") {
            const hasPassword = this.tienePasswordConfigurada;
            return {
               ...method,
               linked: hasPassword,
               principal: hasPassword,
               accountLabel: this.currentUser.email,
               description: hasPassword ? "Inicia sesión con tu correo y contraseña" : "Configura una contraseña para ingresar con tu correo",
            };
         }

         const linked = linkedProviders.get(method.id);
         return {
            ...method,
            linked: !!linked,
            accountLabel: linked?.emailProveedor || linked?.nombreProveedor || "",
            identityId: linked?.usuarioIdentidadAccesoId,
         } as AccessMethod;
      });
   }

   loadEmpresasYPermisos(): void {
      try {
         const emisoresJson = this.localStorage.getEmisores();
         const sucursalesJson = this.localStorage.getSucursales();
         this.emisores = emisoresJson ? (JSON.parse(emisoresJson) as Emisor[]) : [];
         this.sucursales = sucursalesJson ? (JSON.parse(sucursalesJson) as Sucursal[]) : [];

         this.empresasPermisos = this.emisores.map((emisor) => ({
            emisor,
            sucursales: this.sucursales.filter((s) => s.emisorId === emisor.emisorId),
         }));
      } catch {
         this.emisores = [];
         this.sucursales = [];
         this.empresasPermisos = [];
      }
   }

   computeInitials(): void {
      const nombre = (this.currentUser.nombre || "").trim();
      const apellidos = (this.currentUser.apellidos || "").trim();
      const first = nombre.length > 0 ? nombre.charAt(0) : "";
      const second = apellidos.length > 0 ? apellidos.charAt(0) : "";
      this.initials = (first + second).toUpperCase() || "U";
   }

   get currentEmisor(): Emisor | undefined {
      return this.emisores.find((e) => e.emisorId === this.emisorId);
   }

   get sucursalesEmpresaActual(): Sucursal[] {
      return this.sucursales.filter((s) => s.emisorId === this.emisorId);
   }

   createForm(): void {
      this.usuarioForm = this.formBuilder.group({
         rutString: ["", [Validators.required, VerificacionRutValidator]],
         nombre: ["", Validators.required],
         apellidos: ["", Validators.required],
         cargo: [""],
         comuna: ["", Validators.required],
         direccion: ["", Validators.required],
         telefono: [0, [Validators.required, Validators.minLength(9), Validators.maxLength(9)]],
         // email se omite intencionalmente: el correo no se permite modificar desde Mi Cuenta
         // (mismo criterio que el RUT). Para cambiarlo se debe usar el flujo de soporte.
      });
   }

   createPassForm(): void {
      this.passForm = this.formBuilder.group({
         currentPass: [""],
         pass: [
            "",
            Validators.compose([
               Validators.required,
               Validators.minLength(this.formConfig.passwordMinLength),
               Validators.maxLength(this.formConfig.passwordMaxLength),
               Validators.pattern(this.formConfig.passwordPatternRegex),
            ]),
         ],
         repeatPass: [
            "",
            Validators.compose([
               Validators.required,
               Validators.minLength(this.formConfig.passwordMinLength),
               Validators.maxLength(this.formConfig.passwordMaxLength),
               Validators.pattern(this.formConfig.passwordPatternRegex),
            ]),
         ],
      });
      this.syncPasswordFormValidators();
   }

   syncPasswordFormValidators(): void {
      if (!this.passForm) {
         return;
      }

      const currentPassControl = this.passForm.get("currentPass");
      if (!currentPassControl) {
         return;
      }

      if (this.tienePasswordConfigurada) {
         currentPassControl.setValidators(
            Validators.compose([
               Validators.required,
               Validators.minLength(this.formConfig.passwordMinLength),
               Validators.maxLength(this.formConfig.passwordMaxLength),
               Validators.pattern(this.formConfig.passwordPatternRegex),
            ])
         );
      } else {
         currentPassControl.clearValidators();
      }

      currentPassControl.updateValueAndValidity({ emitEvent: false });
   }

   setFormData(usuario: Usuario): void {
      this.usuarioForm.get("rutString").patchValue(usuario.rut + "-" + usuario.dv);
      this.usuarioForm.get("nombre").patchValue(usuario.nombre);
      this.usuarioForm.get("apellidos").patchValue(usuario.apellidos);
      this.usuarioForm.get("cargo").patchValue(usuario.cargo);
      this.usuarioForm.get("comuna").patchValue(usuario.comuna);
      this.usuarioForm.get("direccion").patchValue(usuario.direccion);
      this.usuarioForm.get("telefono").patchValue(usuario.telefono);
   }

   enableEditDatos(): void {
      this.datosEditMode = true;
      this.setFormData(this.currentUser);
   }

   cancelEditDatos(): void {
      this.datosEditMode = false;
      this.setFormData(this.currentUser);
   }

   onSubmit(): void {
      if (this.usuarioForm.invalid) {
         return;
      }
      this.loading = true;
      const usuarioObj = new Usuario();
      usuarioObj.usuarioId = this.currentUser.usuarioId;
      usuarioObj.rut = this.currentUser.rut;
      usuarioObj.dv = this.currentUser.dv;
      usuarioObj.activo = this.currentUser.activo;
      usuarioObj.cargo = this.usuarioForm.controls.cargo.value;
      usuarioObj.nombre = this.usuarioForm.controls.nombre.value;
      usuarioObj.apellidos = this.usuarioForm.controls.apellidos.value;
      usuarioObj.telefono = this.usuarioForm.controls.telefono.value;
      usuarioObj.direccion = this.usuarioForm.controls.direccion.value;
      usuarioObj.comuna = this.usuarioForm.controls.comuna.value;
      // El correo no se permite modificar — se mantiene el actual del usuario.
      usuarioObj.email = this.currentUser.email;

      this.userService.updateUser(usuarioObj).subscribe(
         () => {
            this.loading = false;
            this.ref.detectChanges();
            Swal.fire({
               icon: "success",
               title: "Datos actualizados",
               text: "Los datos personales se actualizaron correctamente",
            });
            this.datosEditMode = false;
            this.loadCurrentUser();
            this.loadMiCuenta();
         },
         () => {
            this.loading = false;
            this.ref.detectChanges();
            Swal.fire({
               title: "Error al actualizar",
               text: "No fue posible actualizar los datos, favor intente nuevamente",
               icon: "error",
            });
         }
      );
   }

   //no elimnar
   onCambioUsuarioBaseCompletado(): void {
   }

   onCambioUsuarioBaseCerrado(): void {
      this.setTab("empresas");
   }

   openPassModal(modalRef: any): void {
      this.passForm.reset();
      this.syncPasswordFormValidators();
      this.passModalRef = this.modalService.open(modalRef, {
         windowClass: "",
         size: "lg",
         centered: true,
      });
   }

   closePassModal(): void {
      if (this.passModalRef) {
         this.passModalRef.close();
      }
   }

   changePass(): void {
      if (this.passForm.invalid) {
         return;
      }
      const currentPass = this.tienePasswordConfigurada ? this.passForm.controls.currentPass.value : null;
      const pass = this.passForm.controls.pass.value;
      const repeatPass = this.passForm.controls.repeatPass.value;
      if (pass !== repeatPass) {
         Swal.fire({
            title: "Las contraseñas no coinciden",
            text: "La nueva contraseña no coincide con la de confirmación",
            icon: "info",
         });
         return;
      }
      this.loading = true;
      this.authService.changePass(this.currentUser.usuarioId, currentPass, pass).subscribe(
         (res) => {
            this.loading = false;
            this.ref.detectChanges();
            Swal.fire({
               icon: "success",
               title: this.tienePasswordConfigurada ? "Contraseña actualizada" : "Contraseña configurada",
               text: res.message,
            });
            if (this.cuentaResumen) {
               this.cuentaResumen.tienePasswordConfigurada = true;
               this.syncAccessMethods();
               this.syncPasswordFormValidators();
            }
            this.passForm.reset();
            this.closePassModal();
         },
         (err) => {
            this.loading = false;
            this.ref.detectChanges();
            Swal.fire({
               title: "Error al actualizar contraseña",
               text: this.getPasswordErrorMessage(err),
               icon: "error",
            });
         }
      );
   }

   private getPasswordErrorMessage(err: any): string {
      const apiError = err && err.error;
      if (apiError && apiError.message) {
         return apiError.message;
      }

      if (apiError && apiError.errors) {
         const validationMessages = Object.keys(apiError.errors).reduce((messages: string[], field: string) => {
            const fieldMessages = apiError.errors[field];
            return messages.concat(Array.isArray(fieldMessages) ? fieldMessages : [fieldMessages]);
         }, []);
         if (validationMessages.length) {
            return validationMessages.join(" ");
         }
      }

      return "No fue posible actualizar la contraseña. Intenta nuevamente.";
   }

   getMethodStatusLabel(method: AccessMethod): string {
      if (method.id === "email") {
         return method.linked ? "Configurada" : "No configurada";
      }

      return method.linked ? "Vinculado" : "No vinculado";
   }

   showProximamente(feature: string): void {
      Swal.fire({
         icon: "info",
         title: "Próximamente",
         text: `La funcionalidad "${feature}" estará disponible en una próxima versión.`,
      });
   }

   linkAccessMethod(method: AccessMethod): void {
      if (method.id === "email") {
         return;
      }

      const provider = this.toSocialProvider(method.id);
      if (!provider) {
         return;
      }

      sessionStorage.setItem("oauth_flow", "link");
      sessionStorage.setItem("oauth_return_url", "/mi-cuenta?tab=metodos&oauth_result=link");
      this.startOAuthRedirect(provider);
   }

   unlinkAccessMethod(method: AccessMethod): void {
      const identityId = (method as any).identityId;
      if (!identityId) {
         return;
      }

      this.loading = true;
      this.miCuentaService.unlinkAccessMethod(identityId).subscribe(
         () => {
            this.loading = false;
            this.loadMiCuenta();
            Swal.fire({ icon: "success", title: "Metodo desvinculado" });
         },
         (error) => {
            this.loading = false;
            this.ref.detectChanges();
            Swal.fire({
               icon: "error",
               title: "No fue posible desvincular",
               text: error?.error?.errors?.[0] || error?.error?.message || "Intente nuevamente.",
            });
         }
      );
   }

   /** URLs que ya fallaron al cargar — se marcan para no intentar renderizarlas otra vez */
   public failedAvatarUrls = new Set<string>();

   get avatarSrc(): string | null {
      const avatar = this.cuentaResumen?.avatarSeleccionado;
      if (!avatar) {
         return null;
      }

      if (avatar.avatarUrl && !this.failedAvatarUrls.has(avatar.avatarUrl)) {
         return avatar.avatarUrl;
      }

      if (avatar.archivoBase64 && avatar.mimeType) {
         return `data:${avatar.mimeType};base64,${avatar.archivoBase64}`;
      }

      return null;
   }

   avatarSource(avatar: MiCuentaAvatar): string | null {
      if (avatar.avatarUrl && !this.failedAvatarUrls.has(avatar.avatarUrl)) {
         return avatar.avatarUrl;
      }
      if (avatar.archivoBase64 && avatar.mimeType) {
         return `data:${avatar.mimeType};base64,${avatar.archivoBase64}`;
      }
      return null;
   }

   onAvatarImageError(url: string | null): void {
      if (!url || url.startsWith("data:")) {
         return;
      }
      if (!this.failedAvatarUrls.has(url)) {
         this.failedAvatarUrls.add(url);
         this.ref.detectChanges();
      }
   }

   onAvatarFileSelected(event: Event): void {
      const input = event.target as HTMLInputElement;
      const file = input.files && input.files.length > 0 ? input.files[0] : null;
      if (!file) {
         return;
      }

      this.loading = true;
      this.miCuentaService.uploadAvatar(file).subscribe(
         () => {
            this.loading = false;
            this.loadMiCuenta();
            input.value = "";
         },
         (error) => {
            this.loading = false;
            input.value = "";
            this.ref.detectChanges();
            Swal.fire({
               icon: "error",
               title: "No fue posible subir la foto",
               text: error?.error?.errors?.[0] || error?.error?.message || "Intente nuevamente.",
            });
         }
      );
   }

   selectAvatar(avatar: MiCuentaAvatar): void {
      this.loading = true;
      this.miCuentaService.selectAvatar(avatar.usuarioAvatarId).subscribe(
         () => {
            this.loading = false;
            this.loadMiCuenta();
         },
         () => {
            this.loading = false;
            this.ref.detectChanges();
         }
      );
   }

   deleteAvatar(avatar: MiCuentaAvatar): void {
      this.loading = true;
      this.miCuentaService.deleteAvatar(avatar.usuarioAvatarId).subscribe(
         () => {
            this.loading = false;
            this.loadMiCuenta();
         },
         (error) => {
            this.loading = false;
            this.ref.detectChanges();
            Swal.fire({
               icon: "error",
               title: "No fue posible eliminar la foto",
               text: error?.error?.errors?.[0] || error?.error?.message || "Intente nuevamente.",
            });
         }
      );
   }

   sendEmailVerificationCode(isResend: boolean = false): void {
      this.loading = true;
      this.miCuentaService.sendEmailVerificationCode().subscribe(
         () => {
            this.loading = false;
            this.verificationStep = "awaiting-code";
            this.verificationDigits = ["", "", "", "", "", ""];
            this.startVerificationTimer();
            this.ref.detectChanges();
            setTimeout(() => this.focusVerificationDigit(0), 50);
            Swal.fire({
               icon: "success",
               title: isResend ? "Código reenviado" : "Código enviado",
               text: `Revisa tu correo e ingresa el código de 6 dígitos. Tienes ${this.verificationDurationMinutes} minutos.`,
            });
         },
         (error) => {
            this.loading = false;
            this.ref.detectChanges();
            Swal.fire({
               icon: "error",
               title: "No fue posible enviar el código",
               text: error?.error?.errors?.[0] || error?.error?.message || "Intente nuevamente.",
            });
         }
      );
   }

   resendEmailVerificationCode(): void {
      this.sendEmailVerificationCode(true);
   }

   cancelEmailVerification(): void {
      this.verificationStep = "idle";
      this.verificationDigits = ["", "", "", "", "", ""];
      this.stopVerificationTimer();
   }

   private startVerificationTimer(): void {
      this.stopVerificationTimer();
      this.verificationExpiresAt = new Date(Date.now() + this.verificationDurationMinutes * 60 * 1000);
      this.updateRemainingSeconds();
      this.verificationTimer = setInterval(() => {
         this.updateRemainingSeconds();
         if (this.verificationRemainingSeconds <= 0) {
            this.stopVerificationTimer();
         }
         this.ref.detectChanges();
      }, 1000);
   }

   private stopVerificationTimer(): void {
      if (this.verificationTimer) {
         clearInterval(this.verificationTimer);
         this.verificationTimer = null;
      }
   }

   private updateRemainingSeconds(): void {
      if (!this.verificationExpiresAt) {
         this.verificationRemainingSeconds = 0;
         return;
      }
      const diff = Math.floor((this.verificationExpiresAt.getTime() - Date.now()) / 1000);
      this.verificationRemainingSeconds = Math.max(0, diff);
   }

   onVerificationDigitInput(index: number, event: any): void {
      const input = event.target as HTMLInputElement;
      const cleaned = (input.value || "").replace(/\D/g, "");

      // Si el browser dejó pasar más de 1 carácter (algunos navegadores ignoran maxlength al pegar),
      // distribuimos. El paste real va por onVerificationDigitPaste, pero esto es un fallback robusto.
      if (cleaned.length > 1) {
         this.distributeDigits(index, cleaned);
         return;
      }

      this.verificationDigits[index] = cleaned;
      input.value = cleaned;
      if (cleaned && index < this.verificationDigits.length - 1) {
         setTimeout(() => this.focusVerificationDigit(index + 1));
      }
      this.ref.detectChanges();
   }

   onVerificationDigitPaste(index: number, event: ClipboardEvent): void {
      event.preventDefault();
      const clipboard = event.clipboardData || (window as any).clipboardData;
      if (!clipboard) {
         return;
      }
      const text = clipboard.getData("text") || "";
      const digits = text.replace(/\D/g, "");
      if (!digits) {
         return;
      }
      this.distributeDigits(index, digits);
   }

   private distributeDigits(startIndex: number, digits: string): void {
      const total = this.verificationDigits.length;
      // Si el código viene completo, lo pegamos desde el inicio independientemente del índice clickeado
      const useFromStart = digits.length >= total;
      const offset = useFromStart ? 0 : startIndex;
      const chars = digits.split("").slice(0, total - offset);
      // Si pegamos desde el inicio, también limpiamos posibles dígitos posteriores
      if (useFromStart) {
         for (let i = 0; i < total; i++) {
            this.verificationDigits[i] = i < chars.length ? chars[i] : "";
         }
      } else {
         chars.forEach((ch, i) => {
            this.verificationDigits[offset + i] = ch;
         });
      }
      const filledTo = offset + chars.length;
      const nextIndex = Math.min(filledTo, total - 1);
      this.ref.detectChanges();
      // Sincroniza el valor visual de cada input (porque usamos [value] one-way)
      setTimeout(() => {
         for (let i = 0; i < total; i++) {
            const el = document.querySelector("#verif-digit-" + i) as HTMLInputElement | null;
            if (el) {
               el.value = this.verificationDigits[i];
            }
         }
         this.focusVerificationDigit(nextIndex);
      });
   }

   onVerificationDigitKey(index: number, event: KeyboardEvent): void {
      const key = event.key;

      if (key === "Backspace") {
         event.preventDefault();
         if (this.verificationDigits[index]) {
            // Si la casilla actual tiene dígito, la borra y mantiene foco
            this.verificationDigits[index] = "";
            const el = document.querySelector("#verif-digit-" + index) as HTMLInputElement | null;
            if (el) el.value = "";
            this.ref.detectChanges();
         } else if (index > 0) {
            // Casilla actual vacía: borra la anterior y mueve el foco
            this.verificationDigits[index - 1] = "";
            const prev = document.querySelector("#verif-digit-" + (index - 1)) as HTMLInputElement | null;
            if (prev) prev.value = "";
            this.ref.detectChanges();
            this.focusVerificationDigit(index - 1);
         }
         return;
      }

      if (key === "Delete") {
         event.preventDefault();
         this.verificationDigits[index] = "";
         const el = document.querySelector("#verif-digit-" + index) as HTMLInputElement | null;
         if (el) el.value = "";
         this.ref.detectChanges();
         return;
      }

      if (key === "ArrowLeft" && index > 0) {
         event.preventDefault();
         this.focusVerificationDigit(index - 1);
         return;
      }
      if (key === "ArrowRight" && index < this.verificationDigits.length - 1) {
         event.preventDefault();
         this.focusVerificationDigit(index + 1);
         return;
      }
      if (key === "Enter" && this.verificationCodeValue.length === this.verificationDigits.length) {
         event.preventDefault();
         this.confirmEmailVerificationCode();
         return;
      }
   }

   focusVerificationDigit(index: number): void {
      const el = document.querySelector("#verif-digit-" + index) as HTMLInputElement | null;
      if (el) {
         el.focus();
         el.select();
      }
   }

   confirmEmailVerificationCode(): void {
      if (this.verificationExpired) {
         Swal.fire({
            icon: "warning",
            title: "Código expirado",
            text: "El código de verificación ya no es válido. Solicita uno nuevo.",
         });
         return;
      }

      const code = this.verificationCodeValue;
      if (!code || code.length !== this.verificationDigits.length) {
         return;
      }

      this.loading = true;
      this.miCuentaService.confirmEmailVerificationCode(code).subscribe(
         () => {
            this.loading = false;
            this.verificationCode = "";
            this.verificationDigits = ["", "", "", "", "", ""];
            this.verificationStep = "idle";
            this.stopVerificationTimer();
            this.loadMiCuenta();
            Swal.fire({ icon: "success", title: "Correo verificado" });
         },
         (error) => {
            this.loading = false;
            this.ref.detectChanges();
            Swal.fire({
               icon: "error",
               title: "Código inválido",
               text: error?.error?.errors?.[0] || error?.error?.message || "Intente nuevamente.",
            });
         }
      );
   }

   private toSocialProvider(providerId: string): SocialOAuthProvider | null {
      switch (providerId) {
         case "google":
            return SocialOAuthProvider.Google;
         case "github":
            return SocialOAuthProvider.Github;
         case "linkedin":
            return SocialOAuthProvider.Linkedin;
         case "microsoft":
            return SocialOAuthProvider.Microsoft;
         default:
            return null;
      }
   }

   private startOAuthRedirect(provider: SocialOAuthProvider): void {
      switch (provider) {
         case SocialOAuthProvider.Google:
            if (!environment.googleClientId) {
               this.showOAuthConfigError("Google");
               return;
            }
            this.executeOAuthRedirect(provider, "https://accounts.google.com/o/oauth2/v2/auth", environment.googleClientId, "openid profile email", { access_type: "offline" });
            return;
         case SocialOAuthProvider.Github:
            if (!environment.githubClientId) {
               this.showOAuthConfigError("GitHub");
               return;
            }
            this.executeOAuthRedirect(provider, "https://github.com/login/oauth/authorize", environment.githubClientId, "user:email");
            return;
         case SocialOAuthProvider.Linkedin:
            if (!environment.linkedinClientId) {
               this.showOAuthConfigError("LinkedIn");
               return;
            }
            this.executeOAuthRedirect(provider, "https://www.linkedin.com/oauth/v2/authorization", environment.linkedinClientId, "openid profile email");
            return;
         case SocialOAuthProvider.Microsoft:
            if (!environment.microsoftClientId) {
               this.showOAuthConfigError("Microsoft");
               return;
            }
            this.executeOAuthRedirect(provider, this.getMicrosoftAuthorizeUrl(), environment.microsoftClientId, "openid profile email", { response_mode: "query" });
            return;
      }
   }

   private executeOAuthRedirect(provider: SocialOAuthProvider, authBaseUrl: string, clientId: string, scope: string, extraParams: Record<string, string> = {}): void {
      const state = `${provider}_${Date.now()}_${Math.random().toString(36).substring(2)}`;
      sessionStorage.setItem(`oauth_state_${provider}`, state);
      const params = new URLSearchParams({
         client_id: clientId,
         redirect_uri: this.getOAuthRedirectUri(provider),
         response_type: "code",
         scope,
         state,
         ...extraParams,
      });
      window.location.href = `${authBaseUrl}?${params.toString()}`;
   }

   private getOAuthRedirectUri(provider: SocialOAuthProvider): string {
      const baseUrl = environment.oauthRedirectUrl || window.location.origin + "/auth/";
      const normalizedBase = baseUrl.endsWith("/") ? baseUrl : `${baseUrl}/`;
      return `${normalizedBase}${provider}-callback`;
   }

   private getMicrosoftAuthorizeUrl(): string {
      const tenant = environment.microsoftTenant || "common";
      return `https://login.microsoftonline.com/${tenant}/oauth2/v2.0/authorize`;
   }

   private showOAuthConfigError(providerLabel: string): void {
      Swal.fire({
         icon: "error",
         title: "OAuth no configurado",
         text: `El login con ${providerLabel} no esta configurado en este ambiente.`,
      });
   }

   getRolPrincipal(): string {
      const perfiles = this.currentUser && this.currentUser.perfiles ? this.currentUser.perfiles : [];
      if (perfiles.length > 0) {
         const superAdmin = perfiles.find((p) => p && p.isSuperAdmin === true);
         if (superAdmin && superAdmin.nombre) {
            return superAdmin.nombre;
         }
         const conMasPermisos = [...perfiles].sort((a, b) => {
            const ap = a && a.permisos ? a.permisos.length : 0;
            const bp = b && b.permisos ? b.permisos.length : 0;
            return bp - ap;
         })[0];
         if (conMasPermisos && conMasPermisos.nombre) {
            return conMasPermisos.nombre;
         }
      }
      if (this.currentUser && this.currentUser.perfilUsuario) {
         const partes = this.currentUser.perfilUsuario
            .split(",")
            .map((s) => s.trim())
            .filter((s) => s.length > 0);
         const superAdminStr = partes.find((p) => /super\s*admin/i.test(p));
         if (superAdminStr) {
            return superAdminStr;
         }
         if (partes.length > 0) {
            return partes[0];
         }
         return this.currentUser.perfilUsuario;
      }
      const session = this.tokenService.getUser();
      if (session && (session as any).superAdmin) {
         return "SuperAdministrador";
      }
      return "Administrador";
   }

}
